import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import DOMPurify from "dompurify";
import { useLocation, useNavigate } from "react-router-dom";
import * as Tabs from "@radix-ui/react-tabs";
import { supabase } from "../lib/supabase";
import { useUser } from "../lib/UserContext";
import {
  PatientDirectoryRow,
  usePatientDirectory,
} from "../lib/PatientDirectoryContext";

type FormState = {
  full_name: string;
  phone: string;
  patient_identifier?: string;
  preferred_practitioner?: string;
  comments?: string;
};

type Props = {
  activeDoctor?: { id: string } | null;
};

type VCFPreviewContact = {
  full_name: string;
  phone: string;
  patient_identifier?: string | null;
  notes?: string | null;
  source_phone_raw?: string | null;
};

type VCFSkippedContact = {
  full_name: string;
  source_phone_raw?: string | null;
  candidate_phone_raw?: string | null;
  patient_identifier?: string | null;
  notes?: string | null;
  reason: string;
};

type VCFPreviewResponse = {
  praticien_id: string;
  contacts: VCFPreviewContact[];
  skipped_contacts: number;
  skipped_details?: VCFSkippedContact[];
  skipped_reason_counts?: Record<string, number>;
  total_detected: number;
};

type VCFConfirmResponse = {
  praticien_id: string;
  inserted: number;
  updated: number;
  skipped: number;
  total_received: number;
};

type PlatoDirectoryItem = {
  patient_id: string;
  name?: string | null;
  telephone?: string | null;
  telephone2?: string | null;
  telephone3?: string | null;
  email?: string | null;
  dnd?: boolean | number | null;
  updated_at?: string | null;
  raw?: Record<string, any> | string | null;
};

type PlatoRaw = Record<string, any>;

type DetailField = {
  label: string;
  value: string;
};

type PlatoDirectoryListResponse = {
  success: boolean;
  praticien_id: string;
  items: PlatoDirectoryItem[];
  count: number;
  page: number;
  page_size: number;
};

type PlatoDirectorySyncSummary = {
  success: boolean;
  source: string;
  start_page: number;
  end_page: number;
  page_budget: number;
  pages_scanned: number;
  rows_fetched: number;
  rows_upserted: number;
  has_more: boolean;
  next_page: number | null;
  stop_reason: string;
};

const WHATSAPP_PREFIX = "whatsapp:";
const API_URL = import.meta.env.VITE_API_URL || "https://whatsapp-healthcare.onrender.com";

function normalizePhoneInput(value: string) {
  const trimmed = value.trim();
  const withoutPrefix = trimmed.replace(/^whatsapp:/i, "");
  const compact = withoutPrefix.replace(/\s+/g, "");
  if (!compact) return "";
  return compact.startsWith("+") ? compact : `+${compact}`;
}

function formatPhoneForStorage(value: string) {
  const normalized = normalizePhoneInput(value);
  return normalized ? `${WHATSAPP_PREFIX}${normalized}` : "";
}

function formatPhoneForDisplay(value: string) {
  const normalized = normalizePhoneInput(value);
  return normalized;
}

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isMeaningfulValue(value: unknown) {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value as Record<string, unknown>).length > 0;
  return true;
}

function asText(value: unknown) {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) {
    return value
      .map((item) => asText(item))
      .filter(Boolean)
      .join(", ");
  }
  return "";
}

function parseRaw(item: PlatoDirectoryItem): PlatoRaw {
  if (!item.raw) return {};
  if (typeof item.raw === "string") {
    try {
      const parsed = JSON.parse(item.raw) as PlatoRaw;
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }
  return typeof item.raw === "object" ? item.raw : {};
}

function bestPlatoPhone(item: PlatoDirectoryItem) {
  const candidates = [item.telephone, item.telephone2, item.telephone3];
  for (const phone of candidates) {
    const normalized = normalizePhoneInput(phone || "");
    if (normalized) return normalized;
  }
  return "";
}

function sanitizeAlertsHtml(value: string): string {
  return DOMPurify.sanitize(value, {
    ALLOWED_TAGS: ["b", "i", "em", "strong", "ul", "ol", "li", "br", "p"],
    ALLOWED_ATTR: [],
  });
}

const emptyForm: FormState = {
  full_name: "",
  phone: "",
  patient_identifier: "",
  preferred_practitioner: "",
  comments: "",
};

export default function PatientDirectory({ activeDoctor }: Props) {
  const user = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    setActivePractitionerId,
    loadDirectory,
    getDirectoryFor,
    upsertLocalEntry,
    removeLocalEntry,
  } = usePatientDirectory();

  const [form, setForm] = useState<FormState>(emptyForm);
  const [editing, setEditing] = useState<PatientDirectoryRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("patients-list");
  const prefillsAppliedRef = useRef(false);
  const [practitioners, setPractitioners] = useState<{ id: string; full_name: string }[]>([]);
  const [isLoadingPractitioners, setIsLoadingPractitioners] = useState(false);
  const [vcfFile, setVcfFile] = useState<File | null>(null);
  const [defaultRegion, setDefaultRegion] = useState("SG");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<VCFPreviewResponse | null>(null);
  const [confirmSummary, setConfirmSummary] = useState<VCFConfirmResponse | null>(null);
  const [isPlatoClinic, setIsPlatoClinic] = useState(false);
  const [platoLoading, setPlatoLoading] = useState(false);
  const [platoSyncing, setPlatoSyncing] = useState(false);
  const [platoError, setPlatoError] = useState<string | null>(null);
  const [platoItems, setPlatoItems] = useState<PlatoDirectoryItem[]>([]);
  const [platoPage, setPlatoPage] = useState(1);
  const [platoPageSize] = useState(100);
  const [platoTotalCount, setPlatoTotalCount] = useState(0);
  const [platoSyncSummary, setPlatoSyncSummary] = useState<PlatoDirectorySyncSummary | null>(null);
  const [platoSyncStatus, setPlatoSyncStatus] = useState<string | null>(null);
  const [platoSyncBatchCount, setPlatoSyncBatchCount] = useState(0);
  const [platoSyncTotalFetched, setPlatoSyncTotalFetched] = useState(0);
  const [platoSyncTotalUpserted, setPlatoSyncTotalUpserted] = useState(0);
  const [platoSearchInput, setPlatoSearchInput] = useState("");
  const [platoAppliedSearch, setPlatoAppliedSearch] = useState("");
  const [expandedPlatoPatients, setExpandedPlatoPatients] = useState<Set<string>>(new Set());

  const practitionerId = useMemo(
    () => (user?.role === "admin" ? activeDoctor?.id : user?.id) || null,
    [activeDoctor?.id, user?.id, user?.role]
  );

  useEffect(() => {
    async function fetchPractitioners() {
      if (!practitionerId) {
        setPractitioners([]);
        return;
      }

      setIsLoadingPractitioners(true);
      const { data, error: fetchError } = await supabase
        .from("clinic_practitioners")
        .select("id, full_name")
        .eq("clinic_id", practitionerId)
        .eq("role", "Doctor")
        .order("full_name", { ascending: true });

      if (fetchError) {
        console.error("Unable to load practitioners", fetchError.message);
        setPractitioners([]);
      } else {
        setPractitioners((data || []) as { id: string; full_name: string }[]);
      }
      setIsLoadingPractitioners(false);
    }

    fetchPractitioners();
  }, [practitionerId]);

  useEffect(() => {
    setActivePractitionerId(practitionerId);
    if (practitionerId) {
      loadDirectory(practitionerId);
    }
  }, [loadDirectory, practitionerId, setActivePractitionerId]);

  const fetchPlatoDirectoryList = async (
    currentPractitionerId: string,
    page = 1,
    pageSize = platoPageSize,
    search = ""
  ) => {
    const response = await fetch(`${API_URL}/plato/patient-directory/list`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        praticien_id: currentPractitionerId,
        page,
        page_size: pageSize,
        search: search.trim() || null,
      }),
    });

    const payload = (await response.json()) as PlatoDirectoryListResponse;
    if (!response.ok) {
      throw new Error((payload as { detail?: string })?.detail || "Unable to fetch Plato directory list.");
    }

    const items = Array.isArray(payload?.items) ? payload.items : [];
    setPlatoItems(items);
    setPlatoTotalCount(Number(payload?.count) || 0);
    setPlatoPage(Number(payload?.page) || page);
  };

  useEffect(() => {
    async function fetchPlatoStatus() {
      if (!practitionerId) {
        setIsPlatoClinic(false);
        setPlatoItems([]);
        setPlatoPage(1);
        setPlatoTotalCount(0);
        setPlatoSearchInput("");
        setPlatoAppliedSearch("");
        return;
      }

      setPlatoLoading(true);
      setPlatoError(null);

      try {
        const response = await fetch(`${API_URL}/plato/patient-directory/status`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ praticien_id: practitionerId }),
        });

        const payload = await response.json();
        if (!response.ok) {
          throw new Error(payload?.detail || "Unable to detect Plato clinic status.");
        }

        const enabled = Boolean(payload?.is_plato_clinic);
        setIsPlatoClinic(enabled);
        
        if (enabled) {
          setPlatoPage(1);
          setPlatoSearchInput("");
          setPlatoAppliedSearch("");
          try {
            await fetchPlatoDirectoryList(practitionerId, 1, platoPageSize, "");
          } catch (listErr) {
            const listMessage =
              listErr instanceof Error ? listErr.message : "Unable to fetch Plato directory list.";
            setPlatoError(listMessage);
            setPlatoItems([]);
            setPlatoTotalCount(0);
          }
        } else {
          setPlatoItems([]);
          setPlatoPage(1);
          setPlatoTotalCount(0);
          setPlatoSearchInput("");
          setPlatoAppliedSearch("");
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : "Unexpected error";
        setPlatoError(message);
        setIsPlatoClinic(false);
        setPlatoItems([]);
      } finally {
        setPlatoLoading(false);
      }
    }

    fetchPlatoStatus();
  }, [practitionerId]);

  useEffect(() => {
    if (prefillsAppliedRef.current) return;
    const prefill = (location.state as { patientPrefill?: Partial<FormState> } | null)?.patientPrefill;
    if (!prefill) return;

    setForm((prev) => ({
      ...prev,
      full_name: prefill.full_name?.trim() || prev.full_name,
      phone: prefill.phone ? normalizePhoneInput(prefill.phone) : prev.phone,
    }));
    setActiveTab("patient-setup");
    prefillsAppliedRef.current = true;
  }, [location.state]);

  const patients = useMemo(
    () => getDirectoryFor(practitionerId),
    [getDirectoryFor, practitionerId]
  );
  const filteredPatients = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return patients;
    return patients.filter((patient) => {
      const normalizedPhone = formatPhoneForDisplay(patient.phone).toLowerCase();
      return [
        patient.full_name,
        patient.patient_identifier,
        patient.preferred_practitioner,
        patient.comments,
        normalizedPhone,
      ]
        .filter(Boolean)
        .some((value) => value?.toLowerCase().includes(query));
    });
  }, [patients, searchQuery]);

  const platoTotalPages = Math.max(1, Math.ceil(platoTotalCount / platoPageSize));
  const platoStartIndex = platoTotalCount === 0 ? 0 : (platoPage - 1) * platoPageSize + 1;
  const platoEndIndex = platoTotalCount === 0 ? 0 : Math.min(platoPage * platoPageSize, platoTotalCount);

  const togglePlatoExpanded = (patientId: string) => {
    setExpandedPlatoPatients((prev) => {
      const next = new Set(prev);
      if (next.has(patientId)) next.delete(patientId);
      else next.add(patientId);
      return next;
    });
  };

  const handlePlatoSearch = async () => {
    if (!practitionerId || !isPlatoClinic || platoLoading || platoSyncing) return;

    const trimmedSearch = platoSearchInput.trim();
    setPlatoAppliedSearch(trimmedSearch);
    setPlatoPage(1);
    setPlatoLoading(true);
    setPlatoError(null);

    try {
      await fetchPlatoDirectoryList(practitionerId, 1, platoPageSize, trimmedSearch);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unexpected error";
      setPlatoError(message);
      setPlatoItems([]);
      setPlatoTotalCount(0);
    } finally {
      setPlatoLoading(false);
    }
  };

  const handlePlatoClearSearch = async () => {
    if (!practitionerId || !isPlatoClinic || platoLoading || platoSyncing) return;

    setPlatoSearchInput("");
    setPlatoAppliedSearch("");
    setPlatoPage(1);
    setPlatoLoading(true);
    setPlatoError(null);

    try {
      await fetchPlatoDirectoryList(practitionerId, 1, platoPageSize, "");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unexpected error";
      setPlatoError(message);
      setPlatoItems([]);
      setPlatoTotalCount(0);
    } finally {
      setPlatoLoading(false);
    }
  };

  const handlePlatoSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") return;
    event.preventDefault();
    void handlePlatoSearch();
  };

  const handleSendPlatoPatient = (item: PlatoDirectoryItem) => {
    const phone = bestPlatoPhone(item);
    if (!phone) return;

    navigate("/new-message", {
      state: {
        prefillSingleMessage: {
          whatsappNumber: phone,
          patientName: item.name || "",
        },
      },
    });
  };

  const renderDetailSection = (title: string, fields: DetailField[]) => {
    if (!fields.length) return null;
    return (
      <section className="rounded-md border border-gray-200 bg-white p-3 space-y-2">
        <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1">
          {fields.map((field) => (
            <div key={`${title}-${field.label}`} className="text-sm text-gray-700">
              <span className="font-medium text-gray-900">{field.label}:</span> {field.value}
            </div>
          ))}
        </div>
      </section>
    );
  };

  if (!practitionerId) {
    return (
      <div className="bg-white shadow rounded-xl p-6">
        <h1 className="text-xl font-semibold mb-2">Patient directory</h1>
        <p className="text-gray-600">
          Select a practitioner to manage the patient directory.
        </p>
      </div>
    );
  }

  const handlePreviewImport = async () => {
    if (!practitionerId) return;
    if (!vcfFile) {
      setPreviewError("Please select a .vcf file first.");
      return;
    }

    setPreviewLoading(true);
    setPreviewError(null);
    setConfirmSummary(null);

    const formData = new FormData();
    formData.append("praticien_id", practitionerId);
    formData.append("default_region", defaultRegion.trim().toUpperCase() || "SG");
    formData.append("file", vcfFile);

    try {
      const response = await fetch(`${API_URL}/patients-directory/import/vcf/preview`, {
        method: "POST",
        body: formData,
      });

      const payload = await response.json();
      if (!response.ok) {
        setPreviewError(payload?.detail || "Unable to preview VCF import.");
        setPreviewData(null);
        return;
      }

      setPreviewData(payload as VCFPreviewResponse);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unexpected error";
      setPreviewError(message);
      setPreviewData(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!previewData?.contacts?.length || !practitionerId) return;

    setConfirmLoading(true);
    setPreviewError(null);

    try {
      let inserted = 0;
      let updated = 0;
      let skipped = 0;
      const totalReceived = previewData.contacts.length;

      const existingByPhone = new Map(
        patients.map((patient) => [formatPhoneForStorage(patient.phone), patient])
      );

      for (const contact of previewData.contacts) {
        const storedPhone = formatPhoneForStorage(contact.phone || "");
        if (!storedPhone) {
          skipped += 1;
          continue;
        }

        const payload = {
          full_name: (contact.full_name || "").trim(),
          phone: storedPhone,
          patient_identifier: contact.patient_identifier?.trim() || null,
          preferred_practitioner: null,
          comments: contact.notes?.trim() || null,
          praticien_id: practitionerId,
        };

        const existing = existingByPhone.get(storedPhone);
        const result = existing
          ? await supabase
              .from("patients_directory")
              .update(payload)
              .eq("id", existing.id)
              .select()
              .single()
          : await supabase
              .from("patients_directory")
              .insert(payload)
              .select()
              .single();

        if (result.error || !result.data) {
          setPreviewError(result.error?.message || "Unable to confirm VCF import.");
          return;
        }

        const saved = result.data as PatientDirectoryRow;
        upsertLocalEntry(saved);
        existingByPhone.set(storedPhone, saved);

        if (existing) {
          updated += 1;
        } else {
          inserted += 1;
        }
      }

      setConfirmSummary({
        praticien_id: practitionerId,
        inserted,
        updated,
        skipped,
        total_received: totalReceived,
      });
      setPreviewData(null);
      setVcfFile(null);
      await loadDirectory(practitionerId);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unexpected error";
      setPreviewError(message);
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    const storedPhone = formatPhoneForStorage(form.phone);
    if (!storedPhone) {
      setError("Phone number is required");
      setSaving(false);
      return;
    }

    const payload = {
      full_name: form.full_name.trim(),
      phone: storedPhone,
      patient_identifier: form.patient_identifier?.trim() || null,
      preferred_practitioner: form.preferred_practitioner?.trim() || null,
      comments: form.comments?.trim() || null,
      praticien_id: practitionerId,
    };

    try {
      let result;
      if (editing) {
        result = await supabase
          .from("patients_directory")
          .update(payload)
          .eq("id", editing.id)
          .select()
          .single();
      } else {
        result = await supabase
          .from("patients_directory")
          .insert(payload)
          .select()
          .single();
      }

      if (result.error || !result.data) {
        setError(result.error?.message || "Unable to save patient");
      } else {
        const saved = result.data as PatientDirectoryRow;
        upsertLocalEntry(saved);
        setForm(emptyForm);
        setEditing(null);
        setActiveTab("patients-list");
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unexpected error";
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleSyncPlatoDirectory = async () => {
    if (!practitionerId) return;

    setPlatoSyncing(true);
    setPlatoError(null);
    setPlatoSyncSummary(null);
    setPlatoSyncStatus("Starting Plato directory sync...");
    setPlatoSyncBatchCount(0);
    setPlatoSyncTotalFetched(0);
    setPlatoSyncTotalUpserted(0);

    try {
      let nextPage = 1;
      let batchCount = 0;
      let totalFetched = 0;
      let totalUpserted = 0;
      let lastSummary: PlatoDirectorySyncSummary | null = null;

      while (nextPage) {
        batchCount += 1;

        setPlatoSyncBatchCount(batchCount);
        setPlatoSyncStatus(`Syncing Plato directory — batch ${batchCount}, starting at page ${nextPage}...`);

        const response = await fetch(`${API_URL}/plato/patient-directory/sync`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            praticien_id: practitionerId,
            start_page: nextPage,
            page_budget: 19,
          }),
        });

        const payload = await response.json();
        if (!response.ok) {
          throw new Error(payload?.detail || "Unable to sync Plato directory.");
        }

        const summary = payload as PlatoDirectorySyncSummary;
        lastSummary = summary;

        totalFetched += summary.rows_fetched || 0;
        totalUpserted += summary.rows_upserted || 0;

        setPlatoSyncTotalFetched(totalFetched);
        setPlatoSyncTotalUpserted(totalUpserted);
        setPlatoSyncSummary(summary);

        if (!summary.has_more || !summary.next_page) {
          setPlatoSyncStatus(
            `Sync completed — ${totalUpserted} patients upserted across ${batchCount} batch(es).`
          );
          break;
        }

        nextPage = summary.next_page;

        setPlatoSyncStatus(
          `Batch ${batchCount} completed (pages ${summary.start_page} to ${summary.end_page}). Waiting 90 seconds before continuing from page ${nextPage}...`
        );

        await sleep(90_000);
      }

      setPlatoPage(1);
      await fetchPlatoDirectoryList(practitionerId, 1, platoPageSize, platoAppliedSearch);

      if (lastSummary && !lastSummary.has_more) {
        setPlatoSyncSummary(lastSummary);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unexpected error";
      setPlatoError(message);
      setPlatoSyncStatus(null);
    } finally {
      setPlatoSyncing(false);
    }
  };

  const handlePlatoPreviousPage = async () => {
    if (!practitionerId || platoLoading || platoSyncing || platoPage <= 1) return;

    setPlatoLoading(true);
    setPlatoError(null);
    try {
      await fetchPlatoDirectoryList(practitionerId, platoPage - 1, platoPageSize, platoAppliedSearch);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unexpected error";
      setPlatoError(message);
      setPlatoItems([]);
      setPlatoTotalCount(0);
    } finally {
      setPlatoLoading(false);
    }
  };

  const handlePlatoNextPage = async () => {
    if (!practitionerId || platoLoading || platoSyncing || platoPage >= platoTotalPages) return;

    setPlatoLoading(true);
    setPlatoError(null);
    try {
      await fetchPlatoDirectoryList(practitionerId, platoPage + 1, platoPageSize, platoAppliedSearch);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unexpected error";
      setPlatoError(message);
      setPlatoItems([]);
      setPlatoTotalCount(0);
    } finally {
      setPlatoLoading(false);
    }
  };

  const handleEdit = (entry: PatientDirectoryRow) => {
    setEditing(entry);
    setForm({
      full_name: entry.full_name,
      phone: formatPhoneForDisplay(entry.phone),
      patient_identifier: entry.patient_identifier || "",
      preferred_practitioner: entry.preferred_practitioner || "",
      comments: entry.comments || "",
    });
  };

  const handleDelete = async (entry: PatientDirectoryRow) => {
    const confirmed = window.confirm(
      `Delete ${entry.full_name} from the directory?`
    );
    if (!confirmed) return;

    const { error: deleteError } = await supabase
      .from("patients_directory")
      .delete()
      .eq("id", entry.id);

    if (deleteError) {
      setError(deleteError.message);
      return;
    }

    removeLocalEntry(entry);
    setEditing(null);
    setForm(emptyForm);
  };

  return (
    <div className="bg-white shadow rounded-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Patient directory</h1>
          <p className="text-sm text-gray-600">Manage your patient directory.</p>
        </div>
        {saving && <span className="text-sm text-gray-500">Saving…</span>}
      </div>

      <Tabs.Root value={activeTab} onValueChange={setActiveTab}>
        <Tabs.List className="flex border-b mb-6">
          <Tabs.Trigger
            value="patients-list"
            className="px-4 py-2 text-sm font-medium text-gray-600 data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary"
          >
            Patients list
          </Tabs.Trigger>
          <Tabs.Trigger
            value="patient-setup"
            className="px-4 py-2 text-sm font-medium text-gray-600 data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary"
          >
            Add & import
          </Tabs.Trigger>
          {isPlatoClinic && (
            <Tabs.Trigger
              value="plato-directory"
              className="px-4 py-2 text-sm font-medium text-gray-600 data-[state=active]:text-primary data-[state=active]:border-b-2 data-[state=active]:border-primary"
            >
              Plato directory
            </Tabs.Trigger>
          )}
        </Tabs.List>

        <Tabs.Content value="patients-list" className="space-y-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Patients</h2>
              <p className="text-sm text-gray-600">
                Search by name, phone, identifier, practitioner, or notes.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full sm:w-72 border rounded-md px-3 py-2"
                placeholder="Search patients…"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          <div className="border rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Phone
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Identifier
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Preferred practitioner
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Comments
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {filteredPatients.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-6 text-center text-sm text-gray-500"
                    >
                      {patients.length === 0
                        ? "No patients yet. Add your first entry in the ‘Add & import’ tab."
                        : "No patients match your search."}
                    </td>
                  </tr>
                )}
                {filteredPatients.map((patient) => (
                  <tr key={patient.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {patient.full_name}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {formatPhoneForDisplay(patient.phone)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {patient.patient_identifier || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {patient.preferred_practitioner || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {patient.comments || ""}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 space-x-3">
                      <button
                        type="button"
                        onClick={() => {
                          handleEdit(patient);
                          setActiveTab("patient-setup");
                        }}
                        className="text-primary hover:underline"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(patient)}
                        className="text-red-600 hover:underline"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Tabs.Content>

        <Tabs.Content value="patient-setup" className="space-y-6">
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Full name</label>
              <input
                type="text"
                required
                value={form.full_name}
                onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
                className="w-full border rounded-md px-3 py-2"
                placeholder="Jane Doe"
              />
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Phone (WhatsApp)</label>
              <input
                type="text"
                required
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                className="w-full border rounded-md px-3 py-2"
                placeholder="+6580000000"
              />
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Patient identifier (optional)</label>
              <input
                type="text"
                value={form.patient_identifier}
                onChange={(e) =>
                  setForm((f) => ({ ...f, patient_identifier: e.target.value }))
                }
                className="w-full border rounded-md px-3 py-2"
                placeholder="MRN / chart number"
              />
            </div>

            <div className="space-y-1">
              <label className="text-sm font-medium text-gray-700">Preferred practitioner (optional)</label>
              <select
                value={form.preferred_practitioner}
                onChange={(e) =>
                  setForm((f) => ({ ...f, preferred_practitioner: e.target.value }))
                }
                className="w-full border rounded-md px-3 py-2 bg-white"
              >
                <option value="">Select a doctor</option>
                {form.preferred_practitioner &&
                  !practitioners.some((p) => p.full_name === form.preferred_practitioner) && (
                    <option value={form.preferred_practitioner} className="text-gray-500">
                      {form.preferred_practitioner}
                    </option>
                  )}
                {practitioners.map((practitioner) => (
                  <option key={practitioner.id} value={practitioner.full_name}>
                    {practitioner.full_name}
                  </option>
                ))}
              </select>
              {isLoadingPractitioners && (
                <p className="text-xs text-gray-500">Loading practitioners…</p>
              )}
            </div>

            <div className="space-y-1 md:col-span-2">
              <label className="text-sm font-medium text-gray-700">Comments</label>
              <textarea
                value={form.comments}
                onChange={(e) => setForm((f) => ({ ...f, comments: e.target.value }))}
                className="w-full border rounded-md px-3 py-2"
                rows={3}
                placeholder="Notes about the patient"
              />
            </div>

            <div className="md:col-span-2 flex items-center gap-3">
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-primary text-white rounded-md disabled:opacity-60"
              >
                {editing ? "Update patient" : "Add patient"}
              </button>
              {editing && (
                <button
                  type="button"
                  onClick={() => {
                    setEditing(null);
                    setForm(emptyForm);
                  }}
                  className="text-sm text-gray-600 hover:text-gray-800"
                >
                  Cancel edit
                </button>
              )}
              {editing && (
                <button
                  type="button"
                  onClick={() => handleDelete(editing)}
                  className="text-sm text-red-600 hover:text-red-800"
                >
                  Delete patient
                </button>
              )}
              {error && <span className="text-sm text-red-600">{error}</span>}
            </div>
          </form>

          <div className="border rounded-lg p-4 space-y-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Import contacts (.vcf)</h2>
              <p className="text-sm text-gray-600">
                Upload a VCF file, preview detected contacts, then confirm import (insert/update by phone).
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
              <div className="md:col-span-2 space-y-1">
                <label className="text-sm font-medium text-gray-700">VCF file</label>
                <input
                  type="file"
                  accept=".vcf,text/vcard,text/x-vcard"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    setVcfFile(file);
                    setPreviewData(null);
                    setConfirmSummary(null);
                    setPreviewError(null);
                  }}
                  className="w-full border rounded-md px-3 py-2"
                />
              </div>

              <div className="space-y-1">
                <label className="text-sm font-medium text-gray-700">Default region</label>
                <input
                  type="text"
                  value={defaultRegion}
                  onChange={(e) => setDefaultRegion(e.target.value.toUpperCase())}
                  className="w-full border rounded-md px-3 py-2"
                  placeholder="SG"
                  maxLength={2}
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={handlePreviewImport}
                disabled={previewLoading || !vcfFile}
                className="px-4 py-2 rounded-md border border-primary text-primary disabled:opacity-60"
              >
                {previewLoading ? "Previewing…" : "Preview import"}
              </button>

              <button
                type="button"
                onClick={handleConfirmImport}
                disabled={confirmLoading || !previewData?.contacts?.length}
                className="px-4 py-2 rounded-md bg-primary text-white disabled:opacity-60"
              >
                {confirmLoading ? "Importing…" : "Confirm import"}
              </button>

              {previewError && <span className="text-sm text-red-600">{previewError}</span>}
            </div>

            {previewData && (
              <div className="space-y-2">
                <p className="text-sm text-gray-700">
                  Preview: <strong>{previewData.contacts.length}</strong> valid contact(s),
                  {" "}<strong>{previewData.skipped_contacts}</strong> skipped,
                  {" "}<strong>{previewData.total_detected}</strong> detected in file.
                </p>
                <div className="border rounded-md overflow-auto max-h-64">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Identifier</th>
                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {previewData.contacts.slice(0, 200).map((contact, index) => (
                        <tr key={`${contact.phone}-${index}`}>
                          <td className="px-3 py-2 text-sm text-gray-900">{contact.full_name || "—"}</td>
                          <td className="px-3 py-2 text-sm text-gray-700">{contact.phone}</td>
                          <td className="px-3 py-2 text-sm text-gray-700">{contact.patient_identifier || "—"}</td>
                          <td className="px-3 py-2 text-sm text-gray-700">{contact.notes || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {previewData.contacts.length > 200 && (
                  <p className="text-xs text-gray-500">
                    Showing first 200 contacts in preview table.
                  </p>
                )}

                {Boolean(previewData.skipped_details?.length) && (
                  <div className="space-y-2 pt-2">
                    <h3 className="text-sm font-semibold text-gray-900">Skipped contacts</h3>
                    <p className="text-xs text-gray-600">
                      These entries were detected in the VCF file but skipped during normalization.
                    </p>
                    {Boolean(Object.keys(previewData.skipped_reason_counts || {}).length) && (
                      <div className="rounded-md border border-orange-200 bg-orange-50 p-3">
                        <h4 className="text-sm font-semibold text-orange-900">Skipped reason summary</h4>
                        <ul className="mt-2 list-disc pl-5 text-xs text-orange-800 space-y-1">
                          {Object.entries(previewData.skipped_reason_counts || {})
                            .sort((a, b) => b[1] - a[1])
                            .map(([reason, count]) => (
                              <li key={reason}>
                                <span className="font-medium">{reason}</span>: {count}
                              </li>
                            ))}
                        </ul>
                      </div>
                    )}
                    <div className="border rounded-md overflow-auto max-h-64">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Raw phone field</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Candidate</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Identifier</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                          {previewData.skipped_details?.slice(0, 200).map((contact, index) => (
                            <tr key={`${contact.full_name}-${contact.candidate_phone_raw}-${index}`}>
                              <td className="px-3 py-2 text-sm text-gray-900">{contact.full_name || "—"}</td>
                              <td className="px-3 py-2 text-sm text-gray-700">{contact.source_phone_raw || "—"}</td>
                              <td className="px-3 py-2 text-sm text-gray-700">{contact.candidate_phone_raw || "—"}</td>
                              <td className="px-3 py-2 text-sm text-gray-700">{contact.patient_identifier || "—"}</td>
                              <td className="px-3 py-2 text-sm text-gray-700">{contact.notes || "—"}</td>
                              <td className="px-3 py-2 text-sm text-orange-600">{contact.reason || "—"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {(previewData.skipped_details?.length || 0) > 200 && (
                      <p className="text-xs text-gray-500">Showing first 200 skipped contacts.</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {confirmSummary && (
              <div className="rounded-md bg-green-50 border border-green-200 p-3 text-sm text-green-800">
                Import completed — Added: <strong>{confirmSummary.inserted}</strong>, Updated:{" "}
                <strong>{confirmSummary.updated}</strong>, Skipped: <strong>{confirmSummary.skipped}</strong>.
              </div>
            )}
          </div>
        </Tabs.Content>

        {isPlatoClinic && (
          <Tabs.Content value="plato-directory" className="space-y-4">
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Plato directory</h2>
                  <p className="text-sm text-gray-600">
                    Search, inspect and action Plato patients from your synced directory cache.
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handleSyncPlatoDirectory}
                    disabled={platoSyncing || platoLoading}
                    className="px-4 py-2 rounded-md bg-primary text-white disabled:opacity-60"
                  >
                    {platoSyncing ? "Syncing…" : "Sync Plato directory"}
                  </button>
                  {(platoLoading || platoSyncing) && (
                    <span className="text-sm text-gray-500">Processing…</span>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2 w-full sm:max-w-2xl">
                  <input
                    type="text"
                    value={platoSearchInput}
                    onChange={(e) => setPlatoSearchInput(e.target.value)}
                    onKeyDown={handlePlatoSearchKeyDown}
                    className="w-full border rounded-md px-3 py-2"
                    placeholder="Search by name, phone, email, ID, nationality…"
                  />
                  <button
                    type="button"
                    onClick={handlePlatoSearch}
                    disabled={platoLoading || platoSyncing}
                    className="px-3 py-2 rounded-md border text-sm disabled:opacity-60"
                  >
                    Search
                  </button>
                  <button
                    type="button"
                    onClick={handlePlatoClearSearch}
                    disabled={platoLoading || platoSyncing || (!platoSearchInput && !platoAppliedSearch)}
                    className="px-3 py-2 rounded-md border text-sm disabled:opacity-60"
                  >
                    Clear
                  </button>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handlePlatoPreviousPage}
                    disabled={platoLoading || platoSyncing || platoPage <= 1}
                    className="px-3 py-1 rounded-md border text-sm disabled:opacity-60"
                  >
                    Previous
                  </button>
                  <span className="text-sm text-gray-600">Page {platoPage} / {platoTotalPages}</span>
                  <button
                    type="button"
                    onClick={handlePlatoNextPage}
                    disabled={platoLoading || platoSyncing || platoPage >= platoTotalPages}
                    className="px-3 py-1 rounded-md border text-sm disabled:opacity-60"
                  >
                    Next
                  </button>
                </div>
              </div>

              <div className="space-y-1">
                <p className="text-sm text-gray-600">
                  {platoTotalCount > 0
                    ? platoAppliedSearch
                      ? `Showing ${platoStartIndex} to ${platoEndIndex} of ${platoTotalCount} matching Plato patients`
                      : `Showing ${platoStartIndex} to ${platoEndIndex} of ${platoTotalCount} Plato patients`
                    : platoAppliedSearch
                      ? "No matching Plato patients found."
                      : "No Plato patients found."}
                </p>
                {platoAppliedSearch && (
                  <p className="text-xs text-gray-500">Active search: "{platoAppliedSearch}"</p>
                )}
              </div>
            </div>

            {platoSyncStatus && (
              <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800 space-y-1">
                <p>{platoSyncStatus}</p>
                {(platoSyncBatchCount > 0 || platoSyncTotalFetched > 0 || platoSyncTotalUpserted > 0) && (
                  <div className="text-xs text-blue-700 space-y-1">
                    <p>Batches completed: <strong>{platoSyncBatchCount}</strong></p>
                    <p>Rows fetched so far: <strong>{platoSyncTotalFetched}</strong></p>
                    <p>Rows upserted so far: <strong>{platoSyncTotalUpserted}</strong></p>
                  </div>
                )}
              </div>
            )}

            {platoError && (
              <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{platoError}</div>
            )}

            {platoSyncSummary && (
              <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-700">
                Last batch — Pages <strong>{platoSyncSummary.start_page}</strong> to{" "}
                <strong>{platoSyncSummary.end_page}</strong>, scanned:{" "}
                <strong>{platoSyncSummary.pages_scanned}</strong>, fetched:{" "}
                <strong>{platoSyncSummary.rows_fetched}</strong>, upserted:{" "}
                <strong>{platoSyncSummary.rows_upserted}</strong>, stop reason:{" "}
                <strong>{platoSyncSummary.stop_reason}</strong>.
              </div>
            )}

            <div className="border rounded-lg overflow-hidden">
              <div className="max-h-[62vh] overflow-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                      <th className="px-3 py-2 w-12"></th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Telephone</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Telephone 2</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Telephone 3</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {platoItems.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-3 py-6 text-center text-sm text-gray-500">
                          {platoAppliedSearch ? "No matching Plato patients found." : "No Plato patients found."}
                        </td>
                      </tr>
                    )}
                    {platoItems.map((item) => {
                      const isExpanded = expandedPlatoPatients.has(item.patient_id);
                      const raw = parseRaw(item);
                      const preferredPhone = bestPlatoPhone(item);

                      const overviewFields: DetailField[] = [
                        { label: "Title", value: asText(raw.title) },
                        { label: "Name", value: asText(raw.name || item.name) },
                        { label: "Sex", value: asText(raw.sex) },
                        { label: "DOB", value: asText(raw.dob) },
                        { label: "Nationality", value: asText(raw.nationality) },
                        { label: "Marital status", value: asText(raw.marital_status) },
                        { label: "Occupation", value: asText(raw.occupation) },
                        { label: "Given ID", value: asText(raw.given_id) },
                        { label: "NRIC / NRIC type", value: [asText(raw.nric), asText(raw.nric_type)].filter(Boolean).join(" · ") },
                      ].filter((field) => isMeaningfulValue(field.value));

                      const contactFields: DetailField[] = [
                        { label: "Telephone", value: asText(raw.telephone || item.telephone) },
                        { label: "Telephone 2", value: asText(raw.telephone2 || item.telephone2) },
                        { label: "Telephone 3", value: asText(raw.telephone3 || item.telephone3) },
                        { label: "Email", value: asText(raw.email || item.email) },
                        { label: "Address", value: asText(raw.address) },
                        { label: "Unit no", value: asText(raw.unit_no) },
                        { label: "Postal", value: asText(raw.postal) },
                      ].filter((field) => isMeaningfulValue(field.value));

                      const medicalFields: DetailField[] = [
                        { label: "Allergies", value: asText(raw.allergies) },
                        { label: "Allergies select", value: asText(raw.allergies_select) },
                        { label: "Food allergies", value: asText(raw.food_allergies) },
                        { label: "Food allergies select", value: asText(raw.food_allergies_select) },
                        { label: "G6PD", value: asText(raw.g6pd) },
                        { label: "Notes", value: asText(raw.notes) },
                      ].filter((field) => isMeaningfulValue(field.value));

                      const tagsValue = Array.isArray(raw.tag)
                        ? raw.tag.map((entry: any) => asText(entry?.tag || entry)).filter(Boolean).join(", ")
                        : "";
                      const doctorValue = Array.isArray(raw.doctor)
                        ? raw.doctor.map((doctor: any) => asText(doctor)).filter(Boolean).join(", ")
                        : asText(raw.doctor);
                      const corporateValue = Array.isArray(raw.corporate)
                        ? raw.corporate
                            .map((entry: any) => [asText(entry?.corp), asText(entry?.number)].filter(Boolean).join(" "))
                            .filter(Boolean)
                            .join(", ")
                        : "";

                      const taggingFields: DetailField[] = [
                        { label: "Tags", value: tagsValue },
                        { label: "Doctor", value: doctorValue },
                        { label: "Referred by", value: asText(raw.referred_by) },
                        { label: "Corporate", value: corporateValue },
                      ].filter((field) => isMeaningfulValue(field.value));

                      const nokRows = Array.isArray(raw.nok)
                        ? raw.nok
                            .map((entry: any) => ({
                              name: asText(entry?.name),
                              relation: asText(entry?.relation),
                              contact: asText(entry?.contact),
                            }))
                            .filter((entry: any) => entry.name || entry.relation || entry.contact)
                        : [];

                      const recallRows = Array.isArray(raw.recalls)
                        ? raw.recalls
                            .map((recall: any) => ({
                              date: asText(recall?.date),
                              assign: asText(recall?.assign),
                              template: asText(recall?.template),
                            }))
                            .filter((entry: any) => entry.date || entry.assign || entry.template)
                        : [];

                      const customEntries = Object.entries((raw.custom || {}) as Record<string, any>)
                        .map(([key, value]) => {
                          if (!isMeaningfulValue(value)) return null;
                          if (Array.isArray(value)) {
                            const compact = value.map((entry) => asText(entry)).filter(Boolean).join(", ");
                            if (!compact) return null;
                            return { key, value: compact };
                          }
                          return { key, value: asText(value) };
                        })
                        .filter((entry): entry is { key: string; value: string } => Boolean(entry?.value));

                      const alertsHtml = asText(raw.alerts);

                      return (
                        <Fragment key={item.patient_id}>
                          <tr key={item.patient_id} className="hover:bg-gray-50 align-top">
                            <td className="px-3 py-2">
                              <button
                                type="button"
                                onClick={() => togglePlatoExpanded(item.patient_id)}
                                className="inline-flex items-center justify-center w-6 h-6 rounded border border-gray-300 text-gray-600 hover:bg-gray-100"
                                aria-label={isExpanded ? "Collapse details" : "Expand details"}
                              >
                                {isExpanded ? "−" : "+"}
                              </button>
                            </td>
                            <td className="px-3 py-2 text-sm text-gray-900 font-medium">{item.name || "—"}</td>
                            <td className="px-3 py-2 text-sm text-gray-700">{item.telephone || "—"}</td>
                            <td className="px-3 py-2 text-sm text-gray-700">{item.telephone2 || "—"}</td>
                            <td className="px-3 py-2 text-sm text-gray-700">{item.telephone3 || "—"}</td>
                            <td className="px-3 py-2 text-sm text-gray-700">{item.email || "—"}</td>
                            <td className="px-3 py-2 text-sm text-gray-700">
                              <button
                                type="button"
                                disabled={!preferredPhone}
                                onClick={() => handleSendPlatoPatient(item)}
                                className="px-3 py-1 rounded-md border border-primary text-primary disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Send
                              </button>
                            </td>
                          </tr>

                          {isExpanded && (
                            <tr key={`${item.patient_id}-details`} className="bg-gray-50">
                              <td colSpan={7} className="px-3 py-3">
                                <div className="space-y-3">
                                  {renderDetailSection("Patient overview", overviewFields)}
                                  {renderDetailSection("Contact details", contactFields)}

                                  {(alertsHtml || medicalFields.length > 0) && (
                                    <section className="rounded-md border border-gray-200 bg-white p-3 space-y-2">
                                      <h4 className="text-sm font-semibold text-gray-900">Medical / alerts</h4>
                                      {alertsHtml && (
                                        <div
                                          className="text-sm text-gray-700"
                                          dangerouslySetInnerHTML={{ __html: sanitizeAlertsHtml(alertsHtml) }}
                                        />
                                      )}
                                      {medicalFields.length > 0 && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1">
                                          {medicalFields.map((field) => (
                                            <div key={`medical-${field.label}`} className="text-sm text-gray-700">
                                              <span className="font-medium text-gray-900">{field.label}:</span> {field.value}
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </section>
                                  )}

                                  {renderDetailSection("Tags / doctor / referral", taggingFields)}

                                  {nokRows.length > 0 && (
                                    <section className="rounded-md border border-gray-200 bg-white p-3 space-y-2">
                                      <h4 className="text-sm font-semibold text-gray-900">Next of kin</h4>
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                        {nokRows.map((nok: any, index: number) => (
                                          <div key={`nok-${index}`} className="rounded border border-gray-200 p-2 text-sm text-gray-700">
                                            {nok.name && <p><span className="font-medium text-gray-900">Name:</span> {nok.name}</p>}
                                            {nok.relation && <p><span className="font-medium text-gray-900">Relation:</span> {nok.relation}</p>}
                                            {nok.contact && <p><span className="font-medium text-gray-900">Contact:</span> {nok.contact}</p>}
                                          </div>
                                        ))}
                                      </div>
                                    </section>
                                  )}

                                  {recallRows.length > 0 && (
                                    <section className="rounded-md border border-gray-200 bg-white p-3 space-y-2">
                                      <h4 className="text-sm font-semibold text-gray-900">Recalls</h4>
                                      <ul className="space-y-1 text-sm text-gray-700">
                                        {recallRows.map((recall: any, index: number) => (
                                          <li key={`recall-${index}`}>
                                            {[recall.date, recall.assign && `Assigned: ${recall.assign}`, recall.template && `Template: ${recall.template}`]
                                              .filter(Boolean)
                                              .join(" · ")}
                                          </li>
                                        ))}
                                      </ul>
                                    </section>
                                  )}

                                  {customEntries.length > 0 && (
                                    <section className="rounded-md border border-gray-200 bg-white p-3 space-y-2">
                                      <h4 className="text-sm font-semibold text-gray-900">Custom fields</h4>
                                      <div className="flex flex-wrap gap-2">
                                        {customEntries.map((entry) => (
                                          <span key={`custom-${entry.key}`} className="inline-flex rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-700">
                                            <strong className="mr-1 text-gray-900">{entry.key}:</strong>{entry.value}
                                          </span>
                                        ))}
                                      </div>
                                    </section>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </Tabs.Content>
        )}
      </Tabs.Root>
    </div>
  );
}
